import type {
  BytecodeInstruction,
  ClassInfo,
  MethodInfo,
  RecoveredExpression,
} from "./types";

const MAX_RESULTS = 2_000;
const MAX_VALUE_LENGTH = 16_384;
const MAX_ARRAY_LENGTH = 16_384;
const MAX_STEPS = 20_000;
const MAX_RECURSION = 8;

type ValueKind = "unknown" | "number" | "string" | "array" | "object" | "null";

interface RuntimeValue {
  kind: ValueKind;
  number?: number;
  text?: string;
  items?: RuntimeValue[];
  objectType?: string;
  origins: Set<number>;
  localDependent: boolean;
  transforms: Set<RecoveredExpression["transform"]>;
}

interface ParsedReference {
  owner: string;
  method: string;
  args: string[];
  returns: string;
}

interface EvaluatedCall {
  value: RuntimeValue;
  exact: boolean;
}

interface EvaluationContext {
  classes: Map<string, ClassInfo>;
  memo: Map<string, RuntimeValue | null>;
  active: Set<string>;
}

function unknown(origins: Iterable<number> = []): RuntimeValue {
  return {
    kind: "unknown",
    origins: new Set(origins),
    localDependent: false,
    transforms: new Set(),
  };
}

function numberValue(
  value: number,
  origins: Iterable<number> = [],
): RuntimeValue {
  return {
    kind: "number",
    number: value | 0,
    origins: new Set(origins),
    localDependent: false,
    transforms: new Set(),
  };
}

function stringValue(
  value: string,
  origins: Iterable<number> = [],
  transform?: RecoveredExpression["transform"],
): RuntimeValue {
  return {
    kind: "string",
    text: value.slice(0, MAX_VALUE_LENGTH),
    origins: new Set(origins),
    localDependent: false,
    transforms: new Set(transform ? [transform] : []),
  };
}

function arrayValue(
  length: number,
  origins: Iterable<number> = [],
): RuntimeValue {
  return {
    kind: "array",
    items: Array.from({ length }, () => numberValue(0)),
    origins: new Set(origins),
    localDependent: false,
    transforms: new Set(),
  };
}

function objectValue(
  objectType: string,
  origins: Iterable<number> = [],
): RuntimeValue {
  return {
    kind: "object",
    objectType,
    origins: new Set(origins),
    localDependent: false,
    transforms: new Set(),
  };
}

function nullValue(origins: Iterable<number> = []): RuntimeValue {
  return {
    kind: "null",
    origins: new Set(origins),
    localDependent: false,
    transforms: new Set(),
  };
}

function mergeMetadata(
  output: RuntimeValue,
  inputs: RuntimeValue[],
  offset?: number,
) {
  for (const input of inputs) {
    for (const origin of input.origins) output.origins.add(origin);
    for (const transform of input.transforms) output.transforms.add(transform);
    output.localDependent ||= input.localDependent;
  }
  if (offset !== undefined) output.origins.add(offset);
  return output;
}

function copyScalar(value: RuntimeValue) {
  if (value.kind === "array" || value.kind === "object") return value;
  return {
    ...value,
    origins: new Set(value.origins),
    transforms: new Set(value.transforms),
  };
}

function splitArgs(value: string) {
  if (!value.trim()) return [];
  const parts: string[] = [];
  let depth = 0;
  let start = 0;
  for (let index = 0; index < value.length; index += 1) {
    const character = value[index];
    if (character === "<" || character === "(" || character === "[") depth += 1;
    if (character === ">" || character === ")" || character === "]") {
      depth = Math.max(0, depth - 1);
    }
    if (character === "," && depth === 0) {
      parts.push(value.slice(start, index).trim());
      start = index + 1;
    }
  }
  parts.push(value.slice(start).trim());
  return parts.filter(Boolean);
}

function parseReference(reference?: string): ParsedReference | null {
  if (!reference) return null;
  const arrow = reference.lastIndexOf("→");
  const call = (arrow >= 0 ? reference.slice(0, arrow) : reference).trim();
  const returns = arrow >= 0 ? reference.slice(arrow + 1).trim() : "?";
  const open = call.indexOf("(");
  const close = call.lastIndexOf(")");
  const beforeArgs = open >= 0 ? call.slice(0, open) : call;
  const separator = beforeArgs.lastIndexOf(".");
  if (separator < 0) return null;
  return {
    owner: beforeArgs.slice(0, separator).replaceAll("/", "."),
    method: beforeArgs.slice(separator + 1),
    args:
      open >= 0 && close > open
        ? splitArgs(call.slice(open + 1, close))
        : [],
    returns,
  };
}

function methodDescriptor(reference: ParsedReference) {
  return `(${reference.args.join(", ")}) → ${reference.returns}`;
}

function dynamicArgumentCount(reference?: string) {
  if (!reference) return 0;
  const label = reference.split("·").at(-1)?.trim() ?? reference;
  const open = label.indexOf("(");
  const close = label.indexOf(")", open + 1);
  return open >= 0 && close > open
    ? splitArgs(label.slice(open + 1, close)).length
    : 0;
}

function rebuildDynamicConcat(
  instruction: BytecodeInstruction,
  args: RuntimeValue[],
) {
  const target = `${instruction.bootstrapTarget ?? ""} ${instruction.reference ?? ""}`;
  if (!/StringConcatFactory\.(?:makeConcat|makeConcatWithConstants)|\bmakeConcat(?:WithConstants)?\b/.test(target)) {
    return null;
  }
  const dynamicValues = args.map(
    (value) => asString(value) ?? asNumber(value),
  );
  if (dynamicValues.some((value) => value === null)) return null;
  if (/makeConcatWithConstants/.test(target)) {
    const recipe = instruction.bootstrapArguments?.[0];
    if (typeof recipe !== "string") return null;
    const constants = instruction.bootstrapArguments?.slice(1) ?? [];
    let dynamicIndex = 0;
    let constantIndex = 0;
    let rebuilt = "";
    for (const character of recipe) {
      if (character === "\u0001") {
        const value = dynamicValues[dynamicIndex++];
        if (value === undefined || value === null) return null;
        rebuilt += String(value);
      } else if (character === "\u0002") {
        const value = constants[constantIndex++];
        if (value === undefined) return null;
        rebuilt += String(value);
      } else {
        rebuilt += character;
      }
      if (rebuilt.length > MAX_VALUE_LENGTH) return null;
    }
    if (dynamicIndex !== dynamicValues.length) return null;
    return rebuilt;
  }
  return dynamicValues.map(String).join("");
}

function localIndex(instruction: BytecodeInstruction) {
  const implicit = instruction.mnemonic.match(/_(\d)$/)?.[1];
  if (implicit !== undefined) return Number(implicit);
  const explicit = instruction.operands.match(/(?:local\s+)?(\d+)/)?.[1];
  return explicit === undefined ? 0 : Number(explicit);
}

function integerLiteral(instruction: BytecodeInstruction) {
  if (typeof instruction.literal === "number") return instruction.literal | 0;
  if (instruction.mnemonic === "iconst_m1") return -1;
  const constant = instruction.mnemonic.match(/^[il]const_(\d)$/)?.[1];
  if (constant !== undefined) return Number(constant);
  if (instruction.mnemonic === "bipush" || instruction.mnemonic === "sipush") {
    const value = Number.parseInt(instruction.operands, 10);
    return Number.isFinite(value) ? value | 0 : null;
  }
  return null;
}

function asNumber(value: RuntimeValue) {
  return value.kind === "number" && value.number !== undefined
    ? value.number | 0
    : null;
}

function asString(value: RuntimeValue) {
  if (value.kind === "string") return value.text ?? "";
  if (
    value.kind === "object" &&
    (value.objectType === "java.lang.String" ||
      value.objectType === "java.lang.StringBuilder" ||
      value.objectType === "java.lang.StringBuffer")
  ) {
    return value.text ?? "";
  }
  return null;
}

function known(value: RuntimeValue) {
  return value.kind !== "unknown";
}

function parameterSlots(descriptor: string) {
  const open = descriptor.indexOf("(");
  const close = descriptor.indexOf(")");
  if (open < 0 || close < open) return [];
  return splitArgs(descriptor.slice(open + 1, close));
}

function isWideType(type: string) {
  return type === "long" || type === "double";
}

function methodKey(
  className: string,
  method: MethodInfo,
  args: RuntimeValue[],
) {
  const encoded = args.map((value) => {
    if (value.kind === "number") return `n:${value.number}`;
    if (value.kind === "string") return `s:${value.text}`;
    if (value.kind === "null") return "null";
    if (value.kind === "array") {
      return `a:${value.items
        ?.map((item) =>
          item.kind === "number"
            ? item.number
            : item.kind === "string"
              ? item.text
              : "?",
        )
        .join(",")}`;
    }
    return `${value.kind}:${value.objectType ?? ""}:${value.text ?? ""}`;
  });
  return `${className}#${method.name}${method.descriptor}\u0000${encoded.join("\u0001")}`;
}

function branchTarget(instruction: BytecodeInstruction) {
  const value = Number.parseInt(instruction.operands, 10);
  return Number.isFinite(value) ? value : null;
}

function switchTarget(instruction: BytecodeInstruction, key: number) {
  const pairs = new Map<number, number>();
  for (const match of instruction.operands.matchAll(/(-?\d+):(-?\d+)/g)) {
    pairs.set(Number(match[1]), Number(match[2]));
  }
  const fallback = instruction.operands.match(/default:(-?\d+)/)?.[1];
  return pairs.get(key) ?? (fallback === undefined ? null : Number(fallback));
}

function decodeUtf8(items: RuntimeValue[]) {
  const bytes = items.map((item) => asNumber(item));
  if (bytes.some((value) => value === null)) return null;
  return new TextDecoder("utf-8", { fatal: false }).decode(
    Uint8Array.from(bytes.map((value) => (value as number) & 0xff)),
  );
}

function encodeUtf8(value: string) {
  return Array.from(new TextEncoder().encode(value), (byte) =>
    numberValue(byte),
  );
}

function base64Decode(value: string) {
  try {
    const normalized = value.replaceAll("-", "+").replaceAll("_", "/");
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      "=",
    );
    const decoded = globalThis.atob(padded);
    return Array.from(decoded, (character) =>
      numberValue(character.charCodeAt(0)),
    );
  } catch {
    return null;
  }
}

function rotateLeft(value: number, distance: number) {
  const shift = distance & 31;
  return ((value << shift) | (value >>> (32 - shift))) | 0;
}

function rotateRight(value: number, distance: number) {
  const shift = distance & 31;
  return ((value >>> shift) | (value << (32 - shift))) | 0;
}

function pureInvocation(
  parsed: ParsedReference,
  receiver: RuntimeValue | undefined,
  args: RuntimeValue[],
  instruction: BytecodeInstruction,
): RuntimeValue | null {
  const owner = parsed.owner.toLowerCase();
  const method = parsed.method.toLowerCase();
  const offset = instruction.offset;
  const inputs = [...(receiver ? [receiver] : []), ...args];
  const resultString = (
    value: string,
    transform: RecoveredExpression["transform"] = "constant propagation",
  ) =>
    mergeMetadata(stringValue(value, [], transform), inputs, offset);
  const resultNumber = (value: number) =>
    mergeMetadata(numberValue(value), inputs, offset);
  const resultArray = (items: RuntimeValue[]) =>
    mergeMetadata(
      {
        ...arrayValue(0),
        items,
      },
      inputs,
      offset,
    );

  if (method === "<init>") {
    if (!receiver || receiver.kind !== "object") return null;
    if (
      receiver.objectType === "java.lang.StringBuilder" ||
      receiver.objectType === "java.lang.StringBuffer"
    ) {
      const initial = args.map(asString).find((value) => value !== null) ?? "";
      receiver.text = initial;
      receiver.transforms.add("StringBuilder");
      mergeMetadata(receiver, args, offset);
      return receiver;
    }
    if (receiver.objectType === "java.lang.String") {
      const direct = args.map(asString).find((value) => value !== null);
      const bytes = args.find((value) => value.kind === "array")?.items;
      const decoded = direct ?? (bytes ? decodeUtf8(bytes) : null);
      if (decoded === null) return null;
      receiver.text = decoded;
      receiver.transforms.add(bytes ? "byte-array" : "constant propagation");
      mergeMetadata(receiver, args, offset);
      return receiver;
    }
    return null;
  }

  const receiverString = receiver ? asString(receiver) : null;
  const strings = args.map(asString);
  const numbers = args.map(asNumber);

  if (
    owner === "java.lang.string" &&
    receiverString !== null
  ) {
    if (method === "length") return resultNumber(receiverString.length);
    if (method === "isempty") return resultNumber(receiverString.length ? 0 : 1);
    if (method === "isblank") return resultNumber(receiverString.trim().length ? 0 : 1);
    if (method === "charat" || method === "codepointat") {
      const index = numbers[0];
      if (index === null || index < 0 || index >= receiverString.length) return null;
      return resultNumber(receiverString.charCodeAt(index));
    }
    if (method === "substring") {
      const start = numbers[0];
      const end = numbers[1] ?? receiverString.length;
      if (
        start === null ||
        end === null ||
        start < 0 ||
        end < start ||
        end > receiverString.length
      ) {
        return null;
      }
      return resultString(receiverString.slice(start, end));
    }
    if (method === "concat" && strings[0] !== null) {
      return resultString(
        receiverString + strings[0],
        "string concatenation",
      );
    }
    if (method === "trim") return resultString(receiverString.trim());
    if (method === "strip") return resultString(receiverString.trim());
    if (method === "tolowercase") return resultString(receiverString.toLowerCase());
    if (method === "touppercase") return resultString(receiverString.toUpperCase());
    if (method === "repeat" && numbers[0] !== null && numbers[0] >= 0 && numbers[0] <= 4_096) {
      return resultString(receiverString.repeat(numbers[0]));
    }
    if (method === "replace") {
      if (strings[0] !== null && strings[1] !== null) {
        return resultString(receiverString.split(strings[0]).join(strings[1]));
      }
      if (numbers[0] !== null && numbers[1] !== null) {
        return resultString(
          receiverString
            .split(String.fromCharCode(numbers[0] & 0xffff))
            .join(String.fromCharCode(numbers[1] & 0xffff)),
        );
      }
    }
    if (method === "tochararray") {
      return resultArray(
        Array.from(receiverString, (character) =>
          numberValue(character.charCodeAt(0)),
        ),
      );
    }
    if (method === "getbytes") return resultArray(encodeUtf8(receiverString));
    if (method === "tostring") return resultString(receiverString);
  }

  if (
    (owner === "java.lang.stringbuilder" ||
      owner === "java.lang.stringbuffer") &&
    receiver
  ) {
    const current = asString(receiver);
    if (current === null) return null;
    if (method === "append") {
      const appended =
        strings[0] ??
        (numbers[0] === null
          ? null
          : parsed.args[0] === "char"
            ? String.fromCharCode(numbers[0] & 0xffff)
            : String(numbers[0]));
      if (appended === null) return null;
      receiver.text = (current + appended).slice(0, MAX_VALUE_LENGTH);
      receiver.transforms.add("StringBuilder");
      mergeMetadata(receiver, args, offset);
      return receiver;
    }
    if (method === "tostring") {
      return resultString(current, "StringBuilder");
    }
    if (method === "length") return resultNumber(current.length);
    if (method === "reverse") {
      receiver.text = Array.from(current).reverse().join("");
      receiver.transforms.add("StringBuilder");
      mergeMetadata(receiver, args, offset);
      return receiver;
    }
    if (method === "delete" && numbers[0] !== null && numbers[1] !== null) {
      const start = numbers[0];
      const end = numbers[1];
      if (start < 0 || end < start || end > current.length) return null;
      receiver.text = current.slice(0, start) + current.slice(end);
      receiver.transforms.add("StringBuilder");
      mergeMetadata(receiver, args, offset);
      return receiver;
    }
    if (method === "deletecharat" && numbers[0] !== null) {
      const index = numbers[0];
      if (index < 0 || index >= current.length) return null;
      receiver.text = current.slice(0, index) + current.slice(index + 1);
      receiver.transforms.add("StringBuilder");
      mergeMetadata(receiver, args, offset);
      return receiver;
    }
    if (method === "insert" && numbers[0] !== null) {
      const index = numbers[0];
      const inserted =
        strings[1] ??
        (numbers[1] === null
          ? null
          : parsed.args[1] === "char"
            ? String.fromCharCode(numbers[1] & 0xffff)
            : String(numbers[1]));
      if (inserted === null || index < 0 || index > current.length) return null;
      receiver.text = (
        current.slice(0, index) +
        inserted +
        current.slice(index)
      ).slice(0, MAX_VALUE_LENGTH);
      receiver.transforms.add("StringBuilder");
      mergeMetadata(receiver, args, offset);
      return receiver;
    }
    if (method === "setcharat" && numbers[0] !== null && numbers[1] !== null) {
      const index = numbers[0];
      if (index < 0 || index >= current.length) return null;
      receiver.text =
        current.slice(0, index) +
        String.fromCharCode(numbers[1] & 0xffff) +
        current.slice(index + 1);
      receiver.transforms.add("StringBuilder");
      mergeMetadata(receiver, args, offset);
      return receiver;
    }
  }

  if (
    owner === "java.lang.string" &&
    method === "valueof" &&
    args.length === 1
  ) {
    const value =
      strings[0] ??
      (numbers[0] === null ? null : String(numbers[0]));
    return value === null ? null : resultString(value);
  }
  if (
    (owner === "java.lang.character" && method === "tostring") ||
    (owner === "java.lang.integer" && method === "tostring") ||
    (owner === "java.lang.long" && method === "tostring")
  ) {
    return numbers[0] === null
      ? null
      : resultString(
          owner === "java.lang.character"
            ? String.fromCharCode(numbers[0] & 0xffff)
            : String(numbers[0]),
        );
  }
  if (owner === "java.lang.integer" && method === "rotateleft") {
    return numbers[0] === null || numbers[1] === null
      ? null
      : resultNumber(rotateLeft(numbers[0], numbers[1]));
  }
  if (owner === "java.lang.integer" && method === "rotateright") {
    return numbers[0] === null || numbers[1] === null
      ? null
      : resultNumber(rotateRight(numbers[0], numbers[1]));
  }
  if (owner === "java.lang.integer" && method === "parseint" && strings[0] !== null) {
    const radix = numbers[1] ?? 10;
    const parsed = Number.parseInt(strings[0], radix);
    return Number.isFinite(parsed) ? resultNumber(parsed) : null;
  }
  if (
    owner === "java.lang.integer" &&
    (method === "tohexstring" || method === "tounsignedstring") &&
    numbers[0] !== null
  ) {
    return resultString((numbers[0] >>> 0).toString(method === "tohexstring" ? 16 : 10));
  }
  if (owner === "java.lang.math" && numbers[0] !== null) {
    if (method === "abs") return resultNumber(Math.abs(numbers[0]));
    if (method === "min" && numbers[1] !== null) {
      return resultNumber(Math.min(numbers[0], numbers[1]));
    }
    if (method === "max" && numbers[1] !== null) {
      return resultNumber(Math.max(numbers[0], numbers[1]));
    }
  }
  if (owner === "java.util.objects" && method === "requirenonnull") {
    return args[0]?.kind === "null" ? null : mergeMetadata(copyScalar(args[0]), args, offset);
  }
  if (
    owner === "java.net.urldecoder" &&
    method === "decode" &&
    strings[0] !== null
  ) {
    try {
      return resultString(
        decodeURIComponent(strings[0].replaceAll("+", " ")),
        "constant propagation",
      );
    } catch {
      return null;
    }
  }
  if (owner === "java.util.hexformat" && method === "of") {
    return mergeMetadata(objectValue("java.util.HexFormat"), args, offset);
  }
  if (
    receiver?.objectType === "java.util.HexFormat" &&
    method === "parsehex" &&
    strings[0] !== null
  ) {
    const value = strings[0].replaceAll(/\s+/g, "");
    if (!/^(?:[0-9a-fA-F]{2})+$/.test(value)) return null;
    return resultArray(
      value.match(/.{2}/g)?.map((item) => numberValue(Number.parseInt(item, 16))) ??
        [],
    );
  }
  if (
    owner === "java.util.base64" &&
    (method === "getdecoder" || method === "geturldecoder")
  ) {
    return mergeMetadata(objectValue("java.util.Base64.Decoder"), args, offset);
  }
  if (
    owner === "java.util.base64" &&
    (method === "getencoder" || method === "geturlencoder")
  ) {
    return mergeMetadata(objectValue("java.util.Base64.Encoder"), args, offset);
  }
  if (
    receiver?.objectType === "java.util.Base64.Decoder" &&
    method === "decode" &&
    strings[0] !== null
  ) {
    const decoded = base64Decode(strings[0]);
    if (!decoded) return null;
    const result = resultArray(decoded);
    result.transforms.add("Base64");
    return result;
  }
  if (
    receiver?.objectType === "java.util.Base64.Encoder" &&
    method === "encodetostring" &&
    args[0]?.kind === "array"
  ) {
    const bytes = args[0].items?.map(asNumber);
    if (!bytes || bytes.some((value) => value === null)) return null;
    const binary = String.fromCharCode(
      ...bytes.slice(0, MAX_ARRAY_LENGTH).map((value) => (value as number) & 0xff),
    );
    return resultString(globalThis.btoa(binary), "Base64");
  }
  if (owner === "java.util.arrays" && method === "copyof" && args[0]?.kind === "array") {
    const length = numbers[1];
    if (length === null || length < 0 || length > MAX_ARRAY_LENGTH) return null;
    const items = (args[0].items ?? []).slice(0, length);
    while (items.length < length) items.push(numberValue(0));
    return resultArray(items);
  }
  if (
    owner === "java.util.arrays" &&
    method === "copyofrange" &&
    args[0]?.kind === "array"
  ) {
    const start = numbers[1];
    const end = numbers[2];
    if (
      start === null ||
      end === null ||
      start < 0 ||
      end < start ||
      end - start > MAX_ARRAY_LENGTH
    ) {
      return null;
    }
    const source = args[0].items ?? [];
    const items = source.slice(start, Math.min(end, source.length));
    while (items.length < end - start) items.push(numberValue(0));
    return resultArray(items);
  }
  return null;
}

function findInternalMethod(
  context: EvaluationContext,
  parsed: ParsedReference,
) {
  const classInfo = context.classes.get(parsed.owner);
  if (!classInfo) return null;
  const descriptor = methodDescriptor(parsed);
  const method = classInfo.methods.find(
    (candidate) =>
      candidate.name === parsed.method && candidate.descriptor === descriptor,
  );
  return method ? { classInfo, method } : null;
}

function cloneArgument(value: RuntimeValue) {
  if (value.kind === "array") {
    return {
      ...value,
      items: value.items?.map(copyScalar),
      origins: new Set<number>(),
      transforms: new Set(value.transforms),
      localDependent: false,
    };
  }
  return {
    ...value,
    origins: new Set<number>(),
    transforms: new Set(value.transforms),
    localDependent: false,
  };
}

function evaluateInternalMethod(
  context: EvaluationContext,
  classInfo: ClassInfo,
  method: MethodInfo,
  args: RuntimeValue[],
  depth: number,
  receiver?: RuntimeValue,
): RuntimeValue | null {
  const isStatic = method.access.includes("static");
  if (
    depth > MAX_RECURSION ||
    (!isStatic &&
      !method.access.includes("private") &&
      !method.access.includes("final") &&
      !classInfo.access.includes("final")) ||
    method.bytecodeTruncated ||
    args.some((value) => !known(value))
  ) {
    return null;
  }
  const key = methodKey(classInfo.name, method, args);
  if (context.memo.has(key)) return context.memo.get(key) ?? null;
  if (context.active.has(key)) return null;
  context.active.add(key);

  const stack: RuntimeValue[] = [];
  const locals = new Map<number, RuntimeValue>();
  let slot = 0;
  if (!isStatic) {
    locals.set(0, receiver ? cloneArgument(receiver) : unknown());
    slot = 1;
  }
  const parameterTypes = parameterSlots(method.descriptor);
  for (let index = 0; index < args.length; index += 1) {
    locals.set(slot, cloneArgument(args[index]));
    slot += isWideType(parameterTypes[index] ?? "") ? 2 : 1;
  }
  const offsets = new Map(
    method.instructions.map((instruction, index) => [instruction.offset, index]),
  );
  const pop = () => stack.pop() ?? unknown();
  let cursor = 0;
  let steps = 0;
  let result: RuntimeValue | null = null;

  const jump = (target: number | null) => {
    if (target === null) return false;
    const next = offsets.get(target);
    if (next === undefined) return false;
    cursor = next;
    return true;
  };

  while (
    cursor >= 0 &&
    cursor < method.instructions.length &&
    steps++ < MAX_STEPS
  ) {
    const instruction = method.instructions[cursor];
    const mnemonic = instruction.mnemonic;
    cursor += 1;

    const literalNumber = integerLiteral(instruction);
    if (literalNumber !== null) {
      stack.push(numberValue(literalNumber));
      continue;
    }
    if (
      mnemonic === "ldc" ||
      mnemonic === "ldc_w" ||
      mnemonic === "ldc2_w"
    ) {
      if (typeof instruction.literal === "string") {
        stack.push(stringValue(instruction.literal));
      } else if (typeof instruction.literal === "number") {
        stack.push(numberValue(instruction.literal));
      } else {
        result = null;
        break;
      }
      continue;
    }
    if (mnemonic === "aconst_null") {
      stack.push(nullValue());
      continue;
    }
    if (/^[ailfd]load(?:_\d)?$/.test(mnemonic)) {
      const value = locals.get(localIndex(instruction));
      if (!value) break;
      stack.push(copyScalar(value));
      continue;
    }
    if (/^[ailfd]store(?:_\d)?$/.test(mnemonic)) {
      locals.set(localIndex(instruction), copyScalar(pop()));
      continue;
    }
    if (mnemonic === "iinc") {
      const local = localIndex(instruction);
      const increment = Number(
        instruction.operands.match(/,\s*(-?\d+)/)?.[1] ?? "0",
      );
      const current = asNumber(locals.get(local) ?? unknown());
      if (current === null) break;
      locals.set(local, numberValue(current + increment));
      continue;
    }
    if (mnemonic === "dup") {
      const value = stack.at(-1);
      if (!value) break;
      stack.push(value);
      continue;
    }
    if (mnemonic === "dup_x1" && stack.length >= 2) {
      const right = pop();
      const left = pop();
      stack.push(right, left, right);
      continue;
    }
    if (mnemonic === "dup2" && stack.length >= 2) {
      stack.push(stack[stack.length - 2], stack[stack.length - 1]);
      continue;
    }
    if (mnemonic === "swap" && stack.length >= 2) {
      const right = pop();
      const left = pop();
      stack.push(right, left);
      continue;
    }
    if (mnemonic === "pop" || mnemonic === "pop2") {
      pop();
      if (mnemonic === "pop2") pop();
      continue;
    }
    if (mnemonic === "new") {
      stack.push(objectValue(instruction.reference ?? instruction.operands));
      continue;
    }
    if (mnemonic === "newarray" || mnemonic === "anewarray") {
      const length = asNumber(pop());
      if (length === null || length < 0 || length > MAX_ARRAY_LENGTH) break;
      stack.push(arrayValue(length));
      continue;
    }
    if (mnemonic === "arraylength") {
      const array = pop();
      if (array.kind !== "array" || !array.items) break;
      stack.push(numberValue(array.items.length));
      continue;
    }
    if (/^(?:a|b|c|s|i|l)aload$/.test(mnemonic)) {
      const index = asNumber(pop());
      const array = pop();
      if (
        index === null ||
        array.kind !== "array" ||
        !array.items ||
        index < 0 ||
        index >= array.items.length
      ) {
        break;
      }
      stack.push(copyScalar(array.items[index]));
      continue;
    }
    if (/^(?:a|b|c|s|i|l)astore$/.test(mnemonic)) {
      const value = pop();
      const index = asNumber(pop());
      const array = pop();
      if (
        index === null ||
        array.kind !== "array" ||
        !array.items ||
        index < 0 ||
        index >= array.items.length
      ) {
        break;
      }
      array.items[index] = copyScalar(value);
      continue;
    }
    if (
      /^(?:i|l)(?:add|sub|mul|div|rem|and|or|xor|shl|shr|ushr)$/.test(
        mnemonic,
      )
    ) {
      const right = asNumber(pop());
      const left = asNumber(pop());
      if (left === null || right === null) break;
      let value = 0;
      if (mnemonic.endsWith("add")) value = left + right;
      else if (mnemonic.endsWith("sub")) value = left - right;
      else if (mnemonic.endsWith("mul")) value = Math.imul(left, right);
      else if (mnemonic.endsWith("div")) {
        if (right === 0) break;
        value = (left / right) | 0;
      } else if (mnemonic.endsWith("rem")) {
        if (right === 0) break;
        value = left % right;
      } else if (mnemonic.endsWith("and")) value = left & right;
      else if (mnemonic.endsWith("or")) value = left | right;
      else if (mnemonic.endsWith("xor")) value = left ^ right;
      else if (mnemonic.endsWith("ushr")) value = left >>> (right & 31);
      else if (mnemonic.endsWith("shr")) value = left >> (right & 31);
      else if (mnemonic.endsWith("shl")) value = left << (right & 31);
      stack.push(numberValue(value));
      continue;
    }
    if (/^(?:i|l)neg$/.test(mnemonic)) {
      const value = asNumber(pop());
      if (value === null) break;
      stack.push(numberValue(-value));
      continue;
    }
    if (/^(?:i2[blcs]|l2i)$/.test(mnemonic)) {
      const value = asNumber(pop());
      if (value === null) break;
      if (mnemonic === "i2b") stack.push(numberValue((value << 24) >> 24));
      else if (mnemonic === "i2s") stack.push(numberValue((value << 16) >> 16));
      else if (mnemonic === "i2c") stack.push(numberValue(value & 0xffff));
      else stack.push(numberValue(value));
      continue;
    }
    if (mnemonic === "lcmp") {
      const right = asNumber(pop());
      const left = asNumber(pop());
      if (left === null || right === null) break;
      stack.push(numberValue(left === right ? 0 : left < right ? -1 : 1));
      continue;
    }
    if (/^if(?:eq|ne|lt|le|gt|ge)$/.test(mnemonic)) {
      const value = asNumber(pop());
      if (value === null) break;
      const condition =
        mnemonic === "ifeq"
          ? value === 0
          : mnemonic === "ifne"
            ? value !== 0
            : mnemonic === "iflt"
              ? value < 0
              : mnemonic === "ifle"
                ? value <= 0
                : mnemonic === "ifgt"
                  ? value > 0
                  : value >= 0;
      if (condition && !jump(branchTarget(instruction))) break;
      continue;
    }
    if (/^if_icmp(?:eq|ne|lt|le|gt|ge)$/.test(mnemonic)) {
      const right = asNumber(pop());
      const left = asNumber(pop());
      if (left === null || right === null) break;
      const suffix = mnemonic.slice("if_icmp".length);
      const condition =
        suffix === "eq"
          ? left === right
          : suffix === "ne"
            ? left !== right
            : suffix === "lt"
              ? left < right
              : suffix === "le"
                ? left <= right
                : suffix === "gt"
                  ? left > right
                  : left >= right;
      if (condition && !jump(branchTarget(instruction))) break;
      continue;
    }
    if (mnemonic === "ifnull" || mnemonic === "ifnonnull") {
      const value = pop();
      const condition =
        mnemonic === "ifnull" ? value.kind === "null" : value.kind !== "null";
      if (condition && !jump(branchTarget(instruction))) break;
      continue;
    }
    if (mnemonic === "goto" || mnemonic === "goto_w") {
      if (!jump(branchTarget(instruction))) break;
      continue;
    }
    if (mnemonic === "tableswitch" || mnemonic === "lookupswitch") {
      const value = asNumber(pop());
      if (value === null || !jump(switchTarget(instruction, value))) break;
      continue;
    }
    if (mnemonic === "checkcast" || mnemonic === "nop") continue;
    if (mnemonic === "getstatic") {
      const reference = (instruction.reference ?? "").replaceAll("/", ".");
      if (/StandardCharsets\.UTF_8\b/i.test(reference)) {
        stack.push(objectValue("java.nio.charset.StandardCharsets.UTF_8"));
        continue;
      }
      break;
    }
    if (mnemonic === "invokedynamic") {
      const argumentCount = dynamicArgumentCount(instruction.reference);
      const callArgs = Array.from(
        { length: argumentCount },
        () => pop(),
      ).reverse();
      const rebuilt = rebuildDynamicConcat(instruction, callArgs);
      if (rebuilt === null) break;
      stack.push(
        mergeMetadata(
          stringValue(rebuilt, [], "string concatenation"),
          callArgs,
        ),
      );
      continue;
    }
    if (
      /^(?:invokevirtual|invokespecial|invokestatic|invokeinterface)$/.test(
        mnemonic,
      )
    ) {
      const parsed = parseReference(instruction.reference);
      if (!parsed) break;
      const callArgs = Array.from({ length: parsed.args.length }, () => pop()).reverse();
      const receiver = mnemonic === "invokestatic" ? undefined : pop();
      if (
        callArgs.some((value) => !known(value)) ||
        (receiver && !known(receiver))
      ) {
        break;
      }
      let callResult: RuntimeValue | null = null;
      const internal =
        parsed.method === "<init>" ? null : findInternalMethod(context, parsed);
      if (internal) {
        callResult = evaluateInternalMethod(
          context,
          internal.classInfo,
          internal.method,
          callArgs,
          depth + 1,
          receiver,
        );
      } else {
        callResult = pureInvocation(parsed, receiver, callArgs, instruction);
      }
      if (parsed.method === "<init>") {
        if (!callResult) break;
        continue;
      }
      if (parsed.returns !== "void") {
        if (!callResult) break;
        stack.push(callResult);
      } else if (!callResult && !/^(?:java\.lang\.(?:String|StringBuilder|StringBuffer))$/.test(parsed.owner)) {
        break;
      }
      continue;
    }
    if (/^(?:a|i|l)return$/.test(mnemonic)) {
      result = pop();
      break;
    }
    if (mnemonic === "return") {
      result = null;
      break;
    }

    // Field writes, reflection, native calls, monitors, exceptions and every
    // unsupported opcode stop evaluation. Sample code is never loaded or run.
    result = null;
    break;
  }

  context.active.delete(key);
  const safeResult =
    result &&
    known(result) &&
    (result.kind !== "string" || (result.text?.length ?? 0) <= MAX_VALUE_LENGTH)
      ? result
      : null;
  context.memo.set(key, safeResult);
  return safeResult;
}

function branchTargets(method: MethodInfo) {
  const targets = new Set<number>();
  for (const instruction of method.instructions) {
    if (
      !/^(?:if|goto|jsr|tableswitch|lookupswitch)/.test(
        instruction.mnemonic,
      )
    ) {
      continue;
    }
    for (const match of instruction.operands.matchAll(/-?\d+/g)) {
      targets.add(Number(match[0]));
    }
  }
  return targets;
}

function patchableSlice(
  method: MethodInfo,
  value: RuntimeValue,
  callIndex: number,
) {
  if (value.localDependent || !value.origins.size) return false;
  const startOffset = Math.min(...value.origins);
  const startIndex = method.instructions.findIndex(
    (instruction) => instruction.offset === startOffset,
  );
  if (startIndex < 0 || startIndex > callIndex) return false;
  const targets = branchTargets(method);
  for (let index = startIndex; index <= callIndex; index += 1) {
    const instruction = method.instructions[index];
    if (instruction.mnemonic === "nop") continue;
    if (!value.origins.has(instruction.offset)) return false;
    if (
      index > startIndex &&
      targets.has(instruction.offset)
    ) {
      return false;
    }
    if (
      /^(?:put|get)(?:static|field)$|^(?:if|goto|jsr|ret|switch|monitor|athrow)/.test(
        instruction.mnemonic,
      )
    ) {
      return false;
    }
  }
  return true;
}

function shouldRecord(value: RuntimeValue) {
  const text = asString(value);
  return (
    text !== null &&
    text.length >= 2 &&
    text.length <= MAX_VALUE_LENGTH &&
    value.transforms.size > 0
  );
}

function preferredTransform(value: RuntimeValue) {
  const order: RecoveredExpression["transform"][] = [
    "XOR",
    "Base64",
    "hex",
    "byte-array",
    "StringBuilder",
    "string concatenation",
    "formatting",
    "interprocedural",
    "constant propagation",
  ];
  return order.find((item) => value.transforms.has(item)) ??
    "constant propagation";
}

function scanMethod(
  context: EvaluationContext,
  classInfo: ClassInfo,
  method: MethodInfo,
  output: RecoveredExpression[],
) {
  if (method.bytecodeTruncated || output.length >= MAX_RESULTS) return;
  const stack: RuntimeValue[] = [];
  const locals = new Map<number, RuntimeValue>();
  const targets = branchTargets(method);
  const pop = () => stack.pop() ?? unknown();

  const add = (
    instruction: BytecodeInstruction,
    instructionIndex: number,
    value: RuntimeValue,
    expression: string,
  ) => {
    if (!shouldRecord(value) || output.length >= MAX_RESULTS) return;
    const text = asString(value);
    if (text === null) return;
    const startOffset = value.origins.size
      ? Math.min(...value.origins)
      : undefined;
    output.push({
      id: `constant-eval:${classInfo.name}:${method.name}:${instruction.offset}:${output.length}`,
      entry: classInfo.path,
      className: classInfo.name,
      method: method.name,
      methodDescriptor: method.descriptor,
      offset: instruction.offset,
      sequenceStartOffset: startOffset,
      value: text,
      expression: expression.slice(0, 600),
      transform: preferredTransform(value),
      confidence: "high",
      patchable:
        startOffset !== undefined &&
        patchableSlice(method, value, instructionIndex),
    });
  };

  for (let index = 0; index < method.instructions.length; index += 1) {
    const instruction = method.instructions[index];
    const mnemonic = instruction.mnemonic;
    if (index > 0 && targets.has(instruction.offset)) {
      stack.length = 0;
      locals.clear();
    }
    const literalNumber = integerLiteral(instruction);
    if (literalNumber !== null) {
      stack.push(numberValue(literalNumber, [instruction.offset]));
      continue;
    }
    if (
      mnemonic === "ldc" ||
      mnemonic === "ldc_w" ||
      mnemonic === "ldc2_w"
    ) {
      if (typeof instruction.literal === "string") {
        stack.push(stringValue(instruction.literal, [instruction.offset]));
      } else if (typeof instruction.literal === "number") {
        stack.push(numberValue(instruction.literal, [instruction.offset]));
      } else {
        stack.push(unknown([instruction.offset]));
      }
      continue;
    }
    if (mnemonic === "aconst_null") {
      stack.push(nullValue([instruction.offset]));
      continue;
    }
    if (/^[ailfd]load(?:_\d)?$/.test(mnemonic)) {
      const loaded = copyScalar(
        locals.get(localIndex(instruction)) ?? unknown(),
      );
      // The defining store remains intact. Replacing only this load-through-
      // call slice cannot alter later uses of the local.
      loaded.localDependent = false;
      loaded.origins = new Set([instruction.offset]);
      stack.push(loaded);
      continue;
    }
    if (/^[ailfd]store(?:_\d)?$/.test(mnemonic)) {
      const stored = copyScalar(pop());
      stored.localDependent = true;
      stored.origins.add(instruction.offset);
      locals.set(localIndex(instruction), stored);
      continue;
    }
    if (mnemonic === "iinc") {
      const local = localIndex(instruction);
      const previous = locals.get(local) ?? unknown();
      const increment = Number(
        instruction.operands.match(/,\s*(-?\d+)/)?.[1] ?? "0",
      );
      const value = asNumber(previous);
      const next =
        value === null
          ? unknown([...previous.origins, instruction.offset])
          : mergeMetadata(numberValue(value + increment), [previous], instruction.offset);
      next.localDependent = true;
      locals.set(local, next);
      continue;
    }
    if (mnemonic === "dup") {
      const value = stack.at(-1) ?? unknown();
      value.origins.add(instruction.offset);
      stack.push(value);
      continue;
    }
    if (mnemonic === "dup_x1" && stack.length >= 2) {
      const right = pop();
      const left = pop();
      right.origins.add(instruction.offset);
      left.origins.add(instruction.offset);
      stack.push(right, left, right);
      continue;
    }
    if (mnemonic === "dup2" && stack.length >= 2) {
      const left = stack[stack.length - 2];
      const right = stack[stack.length - 1];
      left.origins.add(instruction.offset);
      right.origins.add(instruction.offset);
      stack.push(left, right);
      continue;
    }
    if (mnemonic === "swap" && stack.length >= 2) {
      const right = pop();
      const left = pop();
      right.origins.add(instruction.offset);
      left.origins.add(instruction.offset);
      stack.push(right, left);
      continue;
    }
    if (mnemonic === "pop" || mnemonic === "pop2") {
      pop();
      if (mnemonic === "pop2") pop();
      continue;
    }
    if (mnemonic === "new") {
      stack.push(
        objectValue(instruction.reference ?? instruction.operands, [
          instruction.offset,
        ]),
      );
      continue;
    }
    if (mnemonic === "newarray" || mnemonic === "anewarray") {
      const lengthValue = pop();
      const length = asNumber(lengthValue);
      if (length === null || length < 0 || length > MAX_ARRAY_LENGTH) {
        stack.push(unknown([...lengthValue.origins, instruction.offset]));
      } else {
        stack.push(
          mergeMetadata(
            arrayValue(length),
            [lengthValue],
            instruction.offset,
          ),
        );
      }
      continue;
    }
    if (mnemonic === "arraylength") {
      const array = pop();
      const value =
        array.kind === "array" && array.items
          ? numberValue(array.items.length)
          : unknown();
      stack.push(mergeMetadata(value, [array], instruction.offset));
      continue;
    }
    if (/^(?:a|b|c|s|i|l)aload$/.test(mnemonic)) {
      const indexValue = pop();
      const array = pop();
      const arrayIndex = asNumber(indexValue);
      const value =
        arrayIndex !== null &&
        array.kind === "array" &&
        array.items &&
        arrayIndex >= 0 &&
        arrayIndex < array.items.length
          ? copyScalar(array.items[arrayIndex])
          : unknown();
      stack.push(
        mergeMetadata(value, [array, indexValue], instruction.offset),
      );
      continue;
    }
    if (/^(?:a|b|c|s|i|l)astore$/.test(mnemonic)) {
      const value = pop();
      const indexValue = pop();
      const array = pop();
      const arrayIndex = asNumber(indexValue);
      mergeMetadata(array, [indexValue, value], instruction.offset);
      if (
        arrayIndex !== null &&
        array.kind === "array" &&
        array.items &&
        arrayIndex >= 0 &&
        arrayIndex < array.items.length
      ) {
        array.items[arrayIndex] = copyScalar(value);
      } else {
        array.kind = "unknown";
        delete array.items;
      }
      continue;
    }
    if (
      /^(?:i|l)(?:add|sub|mul|div|rem|and|or|xor|shl|shr|ushr)$/.test(
        mnemonic,
      )
    ) {
      const rightValue = pop();
      const leftValue = pop();
      const right = asNumber(rightValue);
      const left = asNumber(leftValue);
      let value: RuntimeValue = unknown();
      if (left !== null && right !== null) {
        let calculated = 0;
        if (mnemonic.endsWith("add")) calculated = left + right;
        else if (mnemonic.endsWith("sub")) calculated = left - right;
        else if (mnemonic.endsWith("mul")) calculated = Math.imul(left, right);
        else if (mnemonic.endsWith("div") && right !== 0) calculated = (left / right) | 0;
        else if (mnemonic.endsWith("rem") && right !== 0) calculated = left % right;
        else if (mnemonic.endsWith("and")) calculated = left & right;
        else if (mnemonic.endsWith("or")) calculated = left | right;
        else if (mnemonic.endsWith("xor")) calculated = left ^ right;
        else if (mnemonic.endsWith("ushr")) calculated = left >>> (right & 31);
        else if (mnemonic.endsWith("shr")) calculated = left >> (right & 31);
        else if (mnemonic.endsWith("shl")) calculated = left << (right & 31);
        else value = unknown();
        if (value.kind !== "unknown" || !/(?:div|rem)$/.test(mnemonic) || right !== 0) {
          value = numberValue(calculated);
        }
      }
      stack.push(
        mergeMetadata(value, [leftValue, rightValue], instruction.offset),
      );
      continue;
    }
    if (/^(?:i|l)neg$/.test(mnemonic)) {
      const input = pop();
      const value = asNumber(input);
      stack.push(
        mergeMetadata(
          value === null ? unknown() : numberValue(-value),
          [input],
          instruction.offset,
        ),
      );
      continue;
    }
    if (/^(?:i2[blcs]|l2i)$/.test(mnemonic)) {
      const input = pop();
      const value = asNumber(input);
      let converted = value;
      if (value !== null && mnemonic === "i2b") converted = (value << 24) >> 24;
      if (value !== null && mnemonic === "i2s") converted = (value << 16) >> 16;
      if (value !== null && mnemonic === "i2c") converted = value & 0xffff;
      stack.push(
        mergeMetadata(
          converted === null ? unknown() : numberValue(converted),
          [input],
          instruction.offset,
        ),
      );
      continue;
    }
    if (mnemonic === "arraylength") continue;
    if (mnemonic === "checkcast" || mnemonic === "nop") continue;
    if (mnemonic === "getstatic") {
      const reference = (instruction.reference ?? "").replaceAll("/", ".");
      stack.push(
        /StandardCharsets\.UTF_8\b/i.test(reference)
          ? objectValue("java.nio.charset.StandardCharsets.UTF_8", [
              instruction.offset,
            ])
          : unknown([instruction.offset]),
      );
      continue;
    }
    if (/^(?:getfield)$/.test(mnemonic)) {
      const receiver = pop();
      stack.push(
        mergeMetadata(unknown(), [receiver], instruction.offset),
      );
      continue;
    }
    if (/^(?:putstatic|putfield)$/.test(mnemonic)) {
      pop();
      if (mnemonic === "putfield") pop();
      continue;
    }
    if (
      /^(?:invokevirtual|invokespecial|invokestatic|invokeinterface)$/.test(
        mnemonic,
      )
    ) {
      const parsed = parseReference(instruction.reference);
      if (!parsed) {
        stack.length = 0;
        continue;
      }
      const args = Array.from({ length: parsed.args.length }, () => pop()).reverse();
      const receiver = mnemonic === "invokestatic" ? undefined : pop();
      let evaluated: EvaluatedCall | null = null;
      const internal =
        parsed.method === "<init>" ? null : findInternalMethod(context, parsed);
      if (
        internal &&
        args.every(known)
      ) {
        const internalValue = evaluateInternalMethod(
          context,
          internal.classInfo,
          internal.method,
          args,
          0,
          receiver,
        );
        if (internalValue) {
          const value = mergeMetadata(
            cloneArgument(internalValue),
            args,
            instruction.offset,
          );
          value.transforms.add("interprocedural");
          evaluated = { value, exact: true };
        }
      }
      if (!evaluated) {
        const pure = pureInvocation(parsed, receiver, args, instruction);
        if (pure) evaluated = { value: pure, exact: true };
      }
      if (parsed.method === "<init>") {
        if (
          evaluated &&
          evaluated.exact &&
          shouldRecord(evaluated.value)
        ) {
          add(
            instruction,
            index,
            evaluated.value,
            instruction.reference ?? `${parsed.owner}.${parsed.method}`,
          );
        }
        continue;
      }
      if (parsed.returns !== "void") {
        const value =
          evaluated?.value ??
          mergeMetadata(
            unknown(),
            [...(receiver ? [receiver] : []), ...args],
            instruction.offset,
          );
        stack.push(value);
        if (evaluated?.exact) {
          add(
            instruction,
            index,
            value,
            instruction.reference ?? `${parsed.owner}.${parsed.method}`,
          );
        }
      }
      continue;
    }
    if (mnemonic === "invokedynamic") {
      const argumentCount = dynamicArgumentCount(instruction.reference);
      const args = Array.from({ length: argumentCount }, () => pop()).reverse();
      const rebuilt = rebuildDynamicConcat(instruction, args);
      const result =
        rebuilt !== null
          ? mergeMetadata(
              stringValue(rebuilt, [], "string concatenation"),
              args,
              instruction.offset,
            )
          : mergeMetadata(unknown(), args, instruction.offset);
      stack.push(result);
      if (rebuilt !== null && shouldRecord(result)) {
        output.push({
          id: `constant-eval:${classInfo.name}:${method.name}:${instruction.offset}:indy`,
          entry: classInfo.path,
          className: classInfo.name,
          method: method.name,
          methodDescriptor: method.descriptor,
          offset: instruction.offset,
          value: asString(result) ?? "",
          expression: instruction.reference ?? "invokedynamic",
          transform: "string concatenation",
          confidence: "high",
          patchable: patchableSlice(method, result, index),
          sequenceStartOffset: result.origins.size
            ? Math.min(...result.origins)
            : undefined,
        });
      }
      continue;
    }
    if (
      /^(?:if|goto|jsr|ret|tableswitch|lookupswitch|return|[ailfd]return|athrow)/.test(
        mnemonic,
      )
    ) {
      stack.length = 0;
      locals.clear();
      continue;
    }
  }
}

/**
 * Evaluates only bounded, side-effect-free JVM bytecode over concrete
 * constants. Unsupported instructions terminate the candidate. The analyzed
 * class is never loaded, linked, initialized, or invoked by the host runtime.
 */
export function recoverConstantStringExpressions(classes: ClassInfo[]) {
  const context: EvaluationContext = {
    classes: new Map(classes.map((item) => [item.name, item])),
    memo: new Map(),
    active: new Set(),
  };
  const output: RecoveredExpression[] = [];
  for (const classInfo of classes) {
    for (const method of classInfo.methods) {
      scanMethod(context, classInfo, method, output);
      if (output.length >= MAX_RESULTS) return output;
    }
  }
  const unique = new Map<string, RecoveredExpression>();
  for (const item of output) {
    const key = `${item.entry}\u0000${item.method}\u0000${item.offset}\u0000${item.value}`;
    const previous = unique.get(key);
    if (
      !previous ||
      Number(item.patchable) > Number(previous.patchable) ||
      (item.confidence === "high" && previous.confidence !== "high")
    ) {
      unique.set(key, item);
    }
  }
  return Array.from(unique.values()).slice(0, MAX_RESULTS);
}
